import { Injectable } from '@angular/core';
import { FiMediaBankFileModel } from '@fi-shared/models';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import {
    IInsertMediaUploadFormData,
    IInsertMediaUploadItem,
    IInsertMediaUploadSettings,
} from '@fi-shared/fi-insert-media/fi-insert-media';
import { FiVimeoService } from '@fi-shared/media-bank/fi-vimeo/fi-vimeo.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiBackendService, IBackendErrorData, IBackendUploadProgress } from '@fi-shared/fi-backend/fi-backend.service';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, interval, Observable, Subject } from 'rxjs';
import { FiMediaBankService, IMediaBankFile, IMediaBankFileReference } from '@fi-shared/media-bank';
import { ActionType, FileType, StatusCodeType } from '@fi-shared/types';
import { AppTextService } from '@fi-shared/app-text';
import { MediaBankStorageType } from '@fi-shared/media-bank/media-bank-storage-type';
import { FileTypeService } from '@fi-shared/fi-shared-services/file-type.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { map } from 'rxjs/operators';
import { ActionFailureReasonCode } from '@fi-shared/types/action-failed-reason-code';
import { MediaBankHelperService } from '@fi-shared/media-bank/media-bank-helper.service';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiExifHelperService } from '@fi-shared/fi-insert-media/fi-exif-helper.service';
import { FiMimeTypesMap } from '@fi-shared/utils/get-accept-by-file-type';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';

interface IVimeoFileProcess {
    MediaBankFileId: number;
    VimeoId: string;
}

interface ISilentUploadArgs {
    onUploadProgress?: (mf: IMediaBankFile, progress: number) => void;
    abortUploadFn?: () => void;
    contentLanguageId?: number;
    onUploadStarted?: (uploadItem: IInsertMediaUploadItem) => void;
    skipLoadingAnimation?: boolean;
}

@Injectable()
export class FiFileUpload2Service {
    private _vimeoFilesInProcess$ = new BehaviorSubject<IVimeoFileProcess[]>([]);
    get vimeoFilesInProcessChanged$(): Observable<void> {
        return this._vimeoFilesInProcess$.pipe(map((item) => undefined));
    }

    constructor(
        private contentLanguageService: FiContentLanguageSharedService,
        private vimeoService: FiVimeoService,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private fiBackend: FiBackendService,
        private userDataService: FiUserDataService,
        private fiSnackBarService: FiSnackBarService,
        private fiMediaBankService: FiMediaBankService,
        private fiMediaBankHelperService: MediaBankHelperService,
        private fileTypeService: FileTypeService,
        private loadingService: LoadingService,
        private fiFormatDateService: FiFormatDateService,
        private fiExifHelperService: FiExifHelperService,
    ) {
        interval(1000 * 10).subscribe(() => {
            this._vimeoFilesInProcess$.value.forEach((item) => {
                this.vimeoService.getVideoInfo(item.VimeoId).then((result) => {
                    if (result.isAvailable) {
                        this.raiseVimeoFilesInProcessChanged(item, ActionType.Delete);
                    }
                });
            });
        });
    }

    //#region Process vimeo files
    getVimeoFilesInProcess() {
        return this._vimeoFilesInProcess$.value;
    }

    private raiseVimeoFilesInProcessChanged(item: IVimeoFileProcess, actionType: ActionType) {
        const vimeoFiles = this._vimeoFilesInProcess$.value.slice(0);
        if (actionType === ActionType.Create) {
            vimeoFiles.push(item);
        } else if (actionType === ActionType.Delete) {
            vimeoFiles.splice(vimeoFiles.indexOf(item), 1);
        }
        this._vimeoFilesInProcess$.next(vimeoFiles);
    }
    //#endregion

    //#region Upload files
    silentUploadFiles(files: ArrayLike<File>, args?: ISilentUploadArgs) {
        if (!args) {
            args = {};
        }

        const mediaBankFileReferences: IMediaBankFileReference[] = [];
        let uploadFilesPromise: Promise<FiMediaBankFileModel[]> = this.uploadFiles({
            files: files,
            onUploadFinished: (uploadItem) => {
                const mediaBankFileReference = this.fiMediaBankHelperService.getMediaBankFileReferenceFromMediaFile(
                    uploadItem.mediaBankFile,
                    {
                        isNewUpload: true,
                        showInMediaBank: false,
                        contentLanguageId: args.contentLanguageId,
                    },
                );
                mediaBankFileReferences.push(mediaBankFileReference);
            },
            onUploadProgress: (mf: IMediaBankFile, progress: number) => {
                if (args.onUploadProgress) {
                    args.onUploadProgress(mf, progress);
                }
            },
            onUploadStarted: (uploadItem) => {
                if (args.onUploadStarted) {
                    args.onUploadStarted(uploadItem);
                }
            },
        });

        if (!args.skipLoadingAnimation) {
            uploadFilesPromise = this.loadingService.trackPromise(uploadFilesPromise);
        }

        return new Promise<IMediaBankFileReference[]>((resolve, reject) => {
            uploadFilesPromise.then(() => {
                if (mediaBankFileReferences.length) {
                    resolve(mediaBankFileReferences);
                } else {
                    reject();
                }
            }, reject);
        });
    }

    uploadFiles(settings: IInsertMediaUploadSettings) {
        const uploadData: IInsertMediaUploadItem[] = [];

        Array.from(settings.files).forEach((file: File) => {
            const mediaBankFile = new FiMediaBankFileModel(this.fiFormatDateService);
            const fileName = file.name;
            mediaBankFile.Name = fileName.substr(0, fileName.lastIndexOf('.')) || fileName;
            mediaBankFile.Extension = this.fileTypeService.getFileExtension(fileName).toLowerCase();
            mediaBankFile.FileType = this.fileTypeService.getFileType(file);

            this.contentLanguageService.getContentLanguages().forEach((v) => {
                mediaBankFile.Names.push({
                    ContentLanguageId: v.Id,
                    Value: this.userDataService.userData.PreferredContentLanguage === v.Id ? mediaBankFile.Name : '',
                    SortIndex: v.SortIndex,
                });
                mediaBankFile.Descriptions.push({
                    ContentLanguageId: v.Id,
                    Value: '',
                    SortIndex: v.SortIndex,
                });
            });

            const uploadItem: IInsertMediaUploadItem = {
                file: file,
                mediaBankFile: mediaBankFile,
                percentageLoaded$: new BehaviorSubject<number>(0),
                abortUpload: () => {},
                showInMediaBank: false,
            };
            // todo remove after insert media refactoring
            const mf: any = mediaBankFile;
            mf.$$uploadPogress = {
                abort: () => uploadItem.abortUpload(),
            };
            mf.PercentageLoaded = 0;
            mf.PercentageProcessed = 0;
            uploadItem.percentageLoaded$.subscribe((v) => {
                mf.PercentageLoaded = mf.PercentageProcessed = v;
                if (settings.onUploadProgress) {
                    settings.onUploadProgress(mf, v);
                }
            });

            uploadData.push(uploadItem);
            if (settings.onUploadStarted) {
                settings.onUploadStarted(uploadItem);
            }
        });

        let uploadLimitErrorFileName: string;
        let genericUploadErrorFileNames: string[] = [];
        return new Promise<FiMediaBankFileModel[]>(
            (resolve: (mediaBankFiles: FiMediaBankFileModel[]) => void, reject) => {
                let fileIndex = 0;
                const processLoading = () => {
                    if (fileIndex < uploadData.length) {
                        // form data can be object or function
                        const formData: IInsertMediaUploadFormData = (() => {
                            if (!settings.formData) {
                                return null;
                            }
                            return settings.formData;
                        })();

                        this.uploadFile(
                            uploadData[fileIndex],
                            settings.skipSaveMediaBankOnBackend,
                            settings.url,
                            formData,
                        )
                            .then(
                                () => {
                                    settings.onUploadFinished(uploadData[fileIndex]);
                                },
                                (err: IBackendErrorData) => {
                                    const reason = err?.error?.Reason;

                                    if (settings.onUploadCancelled) {
                                        settings.onUploadCancelled(uploadData[fileIndex]);
                                    }
                                    if (err.status !== StatusCodeType.ClientClosedRequest) {
                                        if (settings.onUploadError) {
                                            settings.onUploadError({
                                                mediaBankFile: uploadData[fileIndex].mediaBankFile,
                                                reasonCode: reason,
                                            });
                                        }

                                        const fileName =
                                            uploadData[fileIndex].mediaBankFile.Name +
                                            uploadData[fileIndex].mediaBankFile.Extension;
                                        if (reason === ActionFailureReasonCode.MediaBank_FileSizeLimitExceeded) {
                                            uploadLimitErrorFileName = fileName;
                                        } else {
                                            genericUploadErrorFileNames.push(fileName);
                                        }
                                    }
                                },
                            )
                            .finally(() => {
                                uploadData[fileIndex].percentageLoaded$.complete();
                                fileIndex++;
                                processLoading();
                            });
                    } else {
                        resolve(uploadData.map((item) => item.mediaBankFile));

                        if (uploadLimitErrorFileName) {
                            const errorMessage = this.appTextService.get('error_message.file_upload_limit_exceed', {
                                '{0}': uploadLimitErrorFileName,
                                '{1}': this.fileTypeService.getFileUploadLimit(uploadLimitErrorFileName),
                            });
                            this.fiDialogsService.error({
                                messageText: errorMessage,
                            });
                        }

                        if (genericUploadErrorFileNames.length) {
                            const errorHeader = this.appTextService.get('error_message.file_upload_error_header');
                            const errorMessage = this.appTextService
                                .get('error_message.file_upload_error_message')
                                .replace('{0}', genericUploadErrorFileNames.join(', '));
                            this.fiDialogsService.error({
                                headerText: errorHeader,
                                messageText: errorMessage,
                            });
                        }
                    }
                };
                processLoading();
            },
        );
    }

    private uploadFile(
        uploadItem: IInsertMediaUploadItem,
        skipSaveMediaBankOnBackend: boolean,
        url?: string,
        formData?: { [prop: string]: any },
    ) {
        const inProcessFile = uploadItem.mediaBankFile;
        return new Promise<void>((resolve: () => void, reject: (err: HttpErrorResponse) => void) => {
            if (uploadItem.file.size > this.fileTypeService.getFileUploadLimit(uploadItem.file.name) * 1024 * 1024) {
                reject(
                    new HttpErrorResponse({
                        status: StatusCodeType.BadRequest,
                        error: {
                            Reason: ActionFailureReasonCode.MediaBank_FileSizeLimitExceeded,
                        },
                    }),
                );
                return;
            }

            const shouldUploadToVimeo = this.vimeoService.shouldUploadToVimeo(inProcessFile);
            if (shouldUploadToVimeo) {
                // upload to vimeo
                this.uploadToVimeoInternal(uploadItem, inProcessFile, skipSaveMediaBankOnBackend, resolve, reject);
            } else {
                // upload to upcloud object storage via backend
                this.uploadToBackendInternal(uploadItem, formData, url, resolve, reject);
            }
        });
    }

    private uploadToBackendInternal(
        uploadItem: IInsertMediaUploadItem,
        formData: { [prop: string]: any },
        url: string,
        resolve: () => void,
        reject: (err: HttpErrorResponse) => void,
    ) {
        if (!formData) {
            formData = { contentLanguageId: this.userDataService.userData.PreferredContentLanguage };
        } else {
            formData.contentLanguageId = this.userDataService.userData.PreferredContentLanguage;
        }

        const promise = this.doUploadToBackend(uploadItem.file, {
            url: url,
            onUploadProgress: (evt) => {
                const percentage = evt.loaded !== evt.total ? (50 * evt.loaded) / evt.total : 50;
                uploadItem.percentageLoaded$.next(percentage);
            },
            formData: formData,
        });
        promise.then(
            (data) => {
                const mbFile = uploadItem.mediaBankFile;
                uploadItem.percentageLoaded$.next(100);

                mbFile.Initialize(data[0], this.fiFormatDateService, { preserveDescriptions: true });
                this.contentLanguageService.initializeContentLanguagesArray(mbFile.Names, mbFile.Names);
                this.contentLanguageService.initializeContentLanguagesArray(mbFile.Descriptions, mbFile.Descriptions);
                resolve();
            },
            (err: HttpErrorResponse) => {
                reject(err);
            },
        );

        uploadItem.abortUpload = ((abortUploadInit) => {
            return () => {
                if (promise.cancel) {
                    promise.cancel();
                    reject(
                        new HttpErrorResponse({
                            // todo move to toFiPromise, ignore at Http error handlers
                            status: StatusCodeType.ClientClosedRequest,
                        }),
                    );
                }
                abortUploadInit();
            };
        })(uploadItem.abortUpload);
    }

    private doUploadToBackend(
        file: File,
        opts?: {
            formData?: { [prop: string]: any };
            onUploadProgress?: (uploadProgress: IBackendUploadProgress) => void;
            url?: string;
        },
    ) {
        if (!opts) {
            opts = {};
        }
        const formData: FormData = new FormData();
        formData.append('file', file, file.name);
        for (let prop in opts.formData || {}) {
            const formItem = opts.formData[prop];
            // properly add arrays to FormData
            if (Array.isArray(formItem)) {
                formItem.forEach((x) => {
                    formData.append(prop + '[]', x);
                });
            } else {
                formData.append(prop, formItem);
            }
        }

        if (!opts.url) {
            opts.url = 'MediaBank/FileUpload/UploadFileV2';
        }
        return this.fiBackend.fetchDataPost<IMediaBankFile>(opts.url, formData, {
            supressErrorMessage: true,
            reqConfig: {
                observe: 'events',
                responseType: 'json',
                reportProgress: true,
            },
            onUploadProgress: opts.onUploadProgress,
        });
    }

    private uploadToVimeoInternal(
        uploadItem: IInsertMediaUploadItem,
        inProcessFile: FiMediaBankFileModel,
        skipSaveMediaBankOnBackend: boolean,
        resolve: () => void,
        reject: (err: HttpErrorResponse) => void,
    ) {
        const uploadOptions = {
            onAbort: new Subject<void>(),
            onProgress: (evt) => {
                uploadItem.percentageLoaded$.next(evt.loaded !== evt.total ? (50 * evt.loaded) / evt.total : 50);
            },
        };
        uploadItem.abortUpload = ((abortUploadInit) => {
            return () => {
                uploadOptions.onAbort.next();
                reject(
                    new HttpErrorResponse({
                        status: StatusCodeType.ClientClosedRequest,
                    }),
                );
                abortUploadInit();
            };
        })(uploadItem.abortUpload);

        this.vimeoService.uploadToVimeo(uploadItem.file, uploadOptions).then(
            (vimeoUploadResult) => {
                inProcessFile.StorageType = MediaBankStorageType.Vimeo;
                inProcessFile.PhysicalFileName = vimeoUploadResult.vimeoId;
                inProcessFile.Extension = FiVimeoService.DEFAULT_VIMEO_EXTENSION; // https://chainformation.atlassian.net/browse/FI-4036
                inProcessFile.CreatedByUserId = this.userDataService.getUserId();
                inProcessFile.CreatedByUserName = this.userDataService.getUserFullName();
                inProcessFile.MediaBankFileSetting.IsNews = false;
                inProcessFile.PublicationSetting.IsShared = false;
                inProcessFile.Size = uploadItem.file.size;
                inProcessFile.Width = vimeoUploadResult.width;
                inProcessFile.Height = vimeoUploadResult.height;
                inProcessFile.FileCreatedOn = vimeoUploadResult.createdTime;
                this.fiExifHelperService.getExifDataFromFile(uploadItem.file).then((exifResult) => {
                    if (exifResult.exifData?.DateTime) {
                        inProcessFile.FileCreatedOn = exifResult.exifData.DateTime.toISOString();
                    }
                    if (skipSaveMediaBankOnBackend) {
                        uploadItem.percentageLoaded$.next(100);
                        resolve();
                    } else {
                        this.fiMediaBankService.saveMediaBankFile(inProcessFile, true).then((id) => {
                            uploadItem.percentageLoaded$.next(100);
                            inProcessFile.Id = id;

                            this.raiseVimeoFilesInProcessChanged(
                                {
                                    MediaBankFileId: inProcessFile.Id,
                                    VimeoId: inProcessFile.PhysicalFileName,
                                },
                                ActionType.Create,
                            );

                            resolve();
                        }, reject);
                    }
                }, reject);
            },
            (err: HttpErrorResponse) => {
                reject(err);
            },
        );
    }
    //#endregion

    //#region Drag & Drop routine
    extractFilesFromDrop(dataTransfer: DataTransfer, fileList: FileList): Promise<File[]> {
        const items: DataTransferItemList = dataTransfer.items,
            files: Array<File> = [];
        return new Promise<File[]>((resolve) => {
            if (items && items.length > 0) {
                for (let i = 0; i < items.length; i++) {
                    if (
                        items[i].webkitGetAsEntry &&
                        items[i].webkitGetAsEntry() &&
                        items[i].webkitGetAsEntry().isDirectory
                    ) {
                        const entry = items[i].webkitGetAsEntry();
                        if (entry != null) {
                            this.traverseFileTree(entry).then((result) => {
                                resolve(result);
                            });
                        }
                    } else {
                        const f = items[i].getAsFile();
                        if (f != null) {
                            files.push(f);
                        }
                        resolve(files);
                    }
                }
            } else {
                if (fileList != null) {
                    for (let j = 0; j < fileList.length; j++) {
                        const file = fileList.item(j);
                        if (file.type || file.size > 0) {
                            files.push(file);
                        }
                    }
                    resolve(files);
                }
            }
        });
    }
    private traverseFileTree(entry): Promise<File[]> {
        return new Promise<File[]>((globalResolve) => {
            const dirReader = entry.createReader();
            dirReader.readEntries((results) => {
                const promises: Array<any> = [];
                let filesResult: Array<File> = [];
                results.forEach((entry) => {
                    if (entry.isDirectory) {
                        const dirPromise = new Promise<void>((resolve) => {
                            this.traverseFileTree(entry).then(function (files) {
                                filesResult = filesResult.concat(files);
                                resolve();
                            });
                        });
                        promises.push(dirPromise);
                    } else {
                        const filePromise = new Promise<void>((resolve) => {
                            entry.file(function (file) {
                                filesResult.push(file);
                                resolve();
                            });
                        });
                        promises.push(filePromise);
                    }
                });
                Promise.all(promises).then(() => globalResolve(filesResult));
            });
        });
    }

    //#endregion

    isUploadAllowed(mimeType: string, allowedMimeTypes: string): boolean {
        if (!allowedMimeTypes)
            // no restriction by file types - upload (including dnd) is allowed
            return true;

        if (mimeType && allowedMimeTypes.includes(mimeType)) {
            return true;
        }

        // workaround: for images allowed mimetype is 'image/*'
        if (mimeType.startsWith('image/') && allowedMimeTypes.includes(FiMimeTypesMap[FileType.Image])) {
            return true;
        }

        return false;
    }

    /*showUnsupportedFileTypeError(fileTypes: FileType[]): MatSnackBarRef<FiSnackbarComponent> {
        let textMessage: string = undefined;
        if (fileTypes?.length === 2 && fileTypes.includes(FileType.Image) && fileTypes.includes(FileType.Movie)) {
            // now there can be restriction only by images and videos
            // later, there can be more restrictions with separate texts
            textMessage = this.appTextService.get('ccmFileUpload.only_image_video_can_be_uploaded_error');
        }
        if (!textMessage) {
            throw new Error(`unknown fileTypes in showUnsupportedFileTypeError:` + JSON.stringify(fileTypes));
        }
        return this.fiSnackBarService.showSnackbar({
            textMessage: textMessage,
            buttonIconClass: 'fa fa-ban',
            isSnackbarBorderShown: true,
        });
    }*/
}
